// crossTabBus.ts
import Vue from 'vue'

/** Unique channel name shared by all tabs of your app */
const CHANNEL_NAME = 'vue-cross-tab-bus'

/* ============================
   Event typing (augment this)
   ============================

   // Example in src/@types/crossTab-events.d.ts:
   declare global {
     interface CrossTabEvents {
       'refresh-version': { version: string }
       'user-logged-out': { userId: number }
       'notify': { title: string; body?: string }
     }
   }
   export {}
*/
declare global {
  interface CrossTabEvents {
    // You extend this in your app
  }
}
type KnownEvent = keyof CrossTabEvents & string
type PayloadOf<E extends string> = E extends KnownEvent ? CrossTabEvents[E] : any

export interface EmitOptions {
  /** Also emit locally in this tab? Default: true */
  local?: boolean
}

type CrossMsg<E extends string = string> = {
  _type: 'ctb-msg'
  event: E
  payload: PayloadOf<E>
}

let installed = false
let channel: BroadcastChannel | null = null

class CrossTabBus extends Vue {
  /** Cross-tab emit (BroadcastChannel). Also emits locally by default. */
  public emitCross<E extends string>(event: E, payload: PayloadOf<E>, opts?: EmitOptions): void {
    const local = opts?.local ?? true

    if (local) {
      // Local listeners (this tab)
      this.$emit(event, payload)
      // Optional channel to distinguish cross-tab in listeners if you ever forward locally:
      // here we keep symmetry and also emit the prefixed channel locally
      this.$emit(`crosstab:${event}`, payload)
    }

    channel?.postMessage({ _type: 'ctb-msg', event, payload } as CrossMsg<E>)
  }

  /** Typed sugar for $on */
  public on<E extends string>(event: E, handler: (payload: PayloadOf<E>) => any) {
    this.$on(event, handler)
    return this
  }

  /** Typed sugar for $once (runs once per tab) */
  public once<E extends string>(event: E, handler: (payload: PayloadOf<E>) => any) {
    this.$once(event, handler)
    return this
  }

  /** Typed sugar for $off */
  public off<E extends string>(event?: E, handler?: (payload: PayloadOf<E>) => any) {
    this.$off(event as any, handler as any)
    return this
  }
}

const bus = new CrossTabBus()

function installChannel() {
  if (installed || typeof window === 'undefined') return
  installed = true

  if (!('BroadcastChannel' in window)) {
    // No fallback by request
    // eslint-disable-next-line no-console
    console.warn('[crossTabBus] BroadcastChannel not available; cross-tab disabled.')
    return
  }

  channel = new BroadcastChannel(CHANNEL_NAME)

  channel.addEventListener('message', (ev: MessageEvent) => {
    const data = ev.data as CrossMsg
    if (!data || data._type !== 'ctb-msg') return

    // Re-emit message into the local Vue bus
    bus.$emit(data.event, data.payload)
    // Also emit a prefixed channel for those who want to listen only to cross-tab origin
    bus.$emit(`crosstab:${data.event}`, data.payload)
  })

  window.addEventListener('beforeunload', () => {
    try {
      channel?.close()
    } catch {
      // Ignore errors on close
    }
  })
}

export default function install(VueCtor: typeof Vue) {
  installChannel()

  // Keep same API as your original plugin
  Object.defineProperties(VueCtor, {
    bus: {
      get() {
        return bus
      }
    }
  })
  Object.defineProperties(VueCtor.prototype, {
    $bus: {
      get() {
        return bus
      }
    }
  })
}

/* ========= Vue module augmentation ========= */
declare module 'vue/types/vue' {
  interface Vue {
    $bus: CrossTabBus
  }
  interface VueConstructor<V extends Vue = Vue> {
    bus: CrossTabBus
  }
}
